import React, { useState, useEffect, useMemo } from 'react';
import { initializeApp } from 'firebase/app';
import { 
  getAuth, 
  signOut, 
  onAuthStateChanged,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword
} from 'firebase/auth';
import { 
  getFirestore, 
  collection, 
  addDoc, 
  onSnapshot,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';

// --- KONFIGURASI FIREBASE & TELEGRAM ---
const firebaseConfig = {
  apiKey: "AIzaSyAz6inF_vyR5Ix9KTz0fChc6brChf6zVvU",
  authDomain: "hutang-96e28.firebaseapp.com",
  projectId: "hutang-96e28",
  storageBucket: "hutang-96e28.firebasestorage.app",
  messagingSenderId: "1085488450413",
  appId: "1:1085488450413:web:2d4df46d53e0d674286864"
};

const TELEGRAM_API_TOKEN = "8142524469:AAGKK5b4KUNPfyjrjJtOXoRfThL_dwz5iXU";
const TELEGRAM_GROUP_ID = "-1003007351704";


// --- Inisialisasi Firebase ---
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// --- DAFTAR NAMA TOKO ---
const STORE_NAMES = [
  "TOTO855", "FANTASTIC4D", "BOS5000", "IRON4D", "PUSAT", 
  "ETHER777", "ELLOSLOT", "PIKASLOT", "CUAN855", "LINK5000", "CCTVSLOT",
  "ANGGI", "PUSAT ADM"
].sort();

// --- KOMPONEN UTAMA ---
export default function App() {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [showStoreSelection, setShowStoreSelection] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const fontLink = document.createElement('link');
    fontLink.href = "https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap";
    fontLink.rel = "stylesheet";
    
    const faLink = document.createElement('link');
    faLink.rel = 'stylesheet';
    faLink.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css';

    document.head.appendChild(fontLink);
    document.head.appendChild(faLink);

    const xlsxScript = document.createElement('script');
    xlsxScript.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    xlsxScript.async = true;
    document.body.appendChild(xlsxScript);

    document.body.style.fontFamily = "'Inter', sans-serif";

    return () => {
      document.head.removeChild(fontLink);
      document.head.removeChild(faLink);
      if (document.body.contains(xlsxScript)) document.body.removeChild(xlsxScript);
    };
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        await handleUserSetup(currentUser);
      } else {
        setUser(null);
        setUserData(null);
      }
      setIsAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  const handleUserSetup = async (firebaseUser) => {
    const userRef = doc(db, 'users', firebaseUser.uid);
    const docSnap = await getDoc(userRef);

    if (!docSnap.exists()) {
      const role = firebaseUser.email.endsWith('@superadmin.dev') ? 'superadmin' : 'user';
      const newUserProfile = {
        uid: firebaseUser.uid,
        email: firebaseUser.email,
        role: role,
        managedStores: [],
      };
      await setDoc(userRef, newUserProfile);
      setUserData(newUserProfile);
      if (role === 'user') setShowStoreSelection(true);
    } else {
      const existingUserData = docSnap.data();
      setUserData(existingUserData);
      if (existingUserData.role === 'user' && (!existingUserData.managedStores || existingUserData.managedStores.length === 0)) {
        setShowStoreSelection(true);
      }
    }
  };
  
  const handleAuthAction = async (action, email, password) => {
    setAuthError(null);
    try {
      if (action === 'register') {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      setAuthError(error.code.replace('auth/', '').replace(/-/g, ' '));
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const handleStoreSelectionSave = async (selectedStores) => {
    if (user) {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, { managedStores: selectedStores });
      setUserData(prev => ({ ...prev, managedStores: selectedStores }));
      setShowStoreSelection(false);
    }
  };

  if (!isAuthReady) return <LoadingSpinner />;
  
  return (
    <div className="bg-gray-900 text-gray-200 min-h-screen">
      <AlertModal show={!!authError} onClose={() => setAuthError(null)} title="Autentikasi Gagal" message={authError} />
      {user ? (
        <>
          <Header userData={userData} onLogout={handleLogout} />
          {showStoreSelection && userData?.role === 'user' && (
            <StoreSelectionModal onSave={handleStoreSelectionSave} />
          )}
          {userData ? <Dashboard userData={userData} /> : <LoadingSpinner />}
        </>
      ) : (
        <LoginPage onAuthAction={handleAuthAction} />
      )}
    </div>
  );
}

// --- KOMPONEN HALAMAN LOGIN ---
function LoginPage({ onAuthAction }) {
  const [isRegistering, setIsRegistering] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!email || !password) return;
    onAuthAction(isRegistering ? 'register' : 'login', email, password);
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 p-4">
      <div className="w-full max-w-md p-8 space-y-8 bg-gray-800/50 backdrop-blur-sm rounded-2xl shadow-2xl border border-gray-700">
        <div className="text-center">
            <i className="fa-solid fa-file-invoice-dollar text-5xl text-cyan-400"></i>
            <h1 className="text-3xl font-bold text-gray-100 mt-4">
            {isRegistering ? 'Buat Akun Baru' : 'Selamat Datang Kembali'}
            </h1>
            <p className="text-gray-400">Aplikasi Pencatatan Hutang</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
            <div className="relative">
                <i className="fa-solid fa-at absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full p-3 pl-12 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 border border-transparent focus:border-cyan-500" placeholder="alamat@email.com" />
            </div>
            <div className="relative">
                <i className="fa-solid fa-lock absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength="6" className="w-full p-3 pl-12 bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500 border border-transparent focus:border-cyan-500" placeholder="••••••••" />
            </div>
            <button type="submit" className="w-full flex justify-center items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white font-bold p-3 rounded-lg tracking-wide transition duration-300 transform hover:scale-105">
                <i className={`fa-solid ${isRegistering ? 'fa-user-plus' : 'fa-right-to-bracket'}`}></i>
                <span>{isRegistering ? 'Daftar' : 'Login'}</span>
            </button>
        </form>
        <div className="text-center">
          <button onClick={() => setIsRegistering(!isRegistering)} className="text-sm text-cyan-400 hover:underline">
            {isRegistering ? 'Sudah punya akun? Login di sini' : 'Belum punya akun? Daftar sekarang'}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- KOMPONEN HEADER ---
function Header({ userData, onLogout }) {
  return (
    <header className="bg-gray-800/70 backdrop-blur-sm p-4 flex justify-between items-center sticky top-0 z-20 border-b border-gray-700/50 shadow-lg">
      <div className="flex items-center gap-3">
        <i className="fa-solid fa-file-invoice-dollar text-2xl text-cyan-400"></i>
        <div>
          <h1 className="text-xl font-bold text-gray-100">Dashboard Hutang</h1>
          <p className="text-xs text-gray-400 -mt-1 truncate max-w-[200px] sm:max-w-full">{userData?.email}</p>
        </div>
      </div>
      <div className="flex items-center gap-2 sm:gap-4">
        <span className={`px-3 py-1 rounded-full text-xs font-semibold hidden sm:block ${userData?.role === 'superadmin' ? 'bg-purple-500/20 text-purple-300 border border-purple-500' : 'bg-cyan-500/20 text-cyan-300 border border-cyan-500'}`}>
          {userData?.role}
        </span>
        <button onClick={onLogout} className="bg-red-600 hover:bg-red-700 text-white font-bold h-10 w-10 flex items-center justify-center rounded-full transition duration-300 transform hover:scale-110">
          <i className="fa-solid fa-right-from-bracket"></i>
        </button>
      </div>
    </header>
  );
}

// --- KOMPONEN MODAL PEMILIHAN TOKO ---
function StoreSelectionModal({ onSave }) {
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState(null);

  const handleToggle = (storeName) => {
    setSelected(prev => prev.includes(storeName) ? prev.filter(s => s !== storeName) : [...prev, storeName]);
  };

  const handleSave = () => {
    if (selected.length > 0) {
      onSave(selected);
    } else {
      setError("Anda harus memilih minimal satu toko.");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <AlertModal show={!!error} onClose={() => setError(null)} title="Input Tidak Valid" message={error} />
      <div className="bg-gray-800 rounded-xl p-6 sm:p-8 max-w-2xl w-full border border-gray-700 shadow-2xl">
        <div className="text-center mb-6">
            <i className="fa-solid fa-store text-4xl text-cyan-400"></i>
            <h2 className="text-xl sm:text-2xl font-bold mt-3 text-gray-100">Pilih Toko yang Anda Kelola</h2>
            <p className="text-gray-400 mt-1 text-sm sm:text-base">Pilihan ini menentukan data transaksi yang bisa Anda akses.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-64 overflow-y-auto pr-2 custom-scrollbar">
          {STORE_NAMES.map(store => (
            <label key={store} className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all border-2 ${selected.includes(store) ? 'bg-cyan-500/20 border-cyan-500 text-white' : 'bg-gray-700/50 border-transparent hover:border-gray-600'}`}>
              <span className="font-medium text-sm">{store}</span>
              <input type="checkbox" checked={selected.includes(store)} onChange={() => handleToggle(store)} className="sr-only" />
              <div className={`w-6 h-6 rounded-md flex items-center justify-center border-2 ${selected.includes(store) ? 'bg-cyan-500 border-cyan-400' : 'bg-gray-600 border-gray-500'}`}>
                  {selected.includes(store) && <i className="fa-solid fa-check text-white text-xs"></i>}
              </div>
            </label>
          ))}
        </div>
        <div className="mt-8 flex justify-end">
          <button onClick={handleSave} className="py-3 px-6 sm:px-8 bg-cyan-600 hover:bg-cyan-700 rounded-lg font-bold text-white transition-transform transform hover:scale-105 flex items-center gap-2 text-sm sm:text-base">
             <i className="fa-solid fa-floppy-disk"></i> Simpan Pilihan
          </button>
        </div>
      </div>
    </div>
  );
}

// --- KOMPONEN DASHBOARD ---
function Dashboard({ userData }) {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTx, setEditingTx] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingTx, setDeletingTx] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [payingTx, setPayingTx] = useState(null);
  const [reportStore, setReportStore] = useState(null);
  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  
  const accessibleStores = useMemo(() => {
    return userData.role === 'superadmin' ? STORE_NAMES : (userData.managedStores || []);
  }, [userData]);

  useEffect(() => {
    if (!userData?.role) return;
    setLoading(true);

    const transactionsRef = collection(db, 'transactions');
    let q;
    if (userData.role === 'superadmin') {
      q = query(transactionsRef, orderBy('createdAt', 'desc'));
    } else if (userData.managedStores?.length > 0) {
      q = query(transactionsRef, where('involvedStores', 'array-contains-any', userData.managedStores));
    } else {
      setTransactions([]);
      setLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(q, (snapshot) => {
      let txData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Firestore sorting might fail without index, so we sort in memory for non-superadmin too if needed
      txData.sort((a, b) => {
          const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
          const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
          return timeB - timeA;
      });
      setTransactions(txData);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching transactions:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [userData]);

  const logActivity = async (action, details) => {
    try {
      await addDoc(collection(db, 'activityLogs'), {
        action,
        details,
        userEmail: userData.email,
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error("Error logging activity:", error);
    }
  };
  
  const handleTransactionSubmit = async (txData, isEditing) => {
    const data = { ...txData, involvedStores: [txData.debtorStore, txData.creditorStore].sort() };
    try {
        if(isEditing) {
            const txRef = doc(db, 'transactions', editingTx.id);
            await updateDoc(txRef, {
                ...data,
                updatedAt: serverTimestamp(),
                updatedBy: userData.email
            });
            logActivity('Update Hutang', `${txData.debtorStore} ke ${txData.creditorStore} - ${formatCurrency(txData.amount)}`);
        } else {
            await addDoc(collection(db, 'transactions'), {
                ...data,
                type: 'hutang',
                createdAt: serverTimestamp(),
                createdBy: userData.email,
            });
            logActivity('Tambah Hutang', `${txData.debtorStore} ke ${txData.creditorStore} - ${formatCurrency(txData.amount)}`);
            sendTelegramNotification(txData, 'hutang');
        }
    } catch (error) {
        console.error("Error saving transaction:", error);
    } finally {
        setShowTransactionModal(false);
        setEditingTx(null);
    }
  };

  const handlePaymentRequest = (tx) => {
    setPayingTx(tx);
    setShowPaymentModal(true);
  };
  
  const handlePaymentSubmit = async (paymentData) => {
    try {
        const newPayment = {
            ...paymentData,
            type: 'bayar',
            createdAt: serverTimestamp(),
            createdBy: userData.email,
            involvedStores: [paymentData.debtorStore, paymentData.creditorStore].sort()
        };
        await addDoc(collection(db, 'transactions'), newPayment);
        logActivity('Bayar Hutang', `${paymentData.debtorStore} ke ${paymentData.creditorStore} - ${formatCurrency(paymentData.amount)}`);
        sendTelegramNotification(paymentData, 'bayar');
    } catch (error) {
        console.error("Error submitting payment:", error);
    } finally {
        setShowPaymentModal(false);
        setPayingTx(null);
    }
  };

  const handleDeleteRequest = (tx) => {
    setDeletingTx(tx);
    setShowDeleteConfirm(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!deletingTx) return;
    try {
      await deleteDoc(doc(db, 'transactions', deletingTx.id));
      logActivity('Hapus Transaksi', `${deletingTx.debtorStore} ke ${deletingTx.creditorStore} (${deletingTx.type}) - ${formatCurrency(deletingTx.amount)}`);
    } catch (error) {
      console.error("Error deleting transaction: ", error);
    } finally {
      setShowDeleteConfirm(false);
      setDeletingTx(null);
    }
  };

  const handleEdit = (tx) => {
    setEditingTx(tx);
    setShowTransactionModal(true);
  };
  
  const filteredTransactions = useMemo(() => {
      if (filter === 'all') return transactions;
      return transactions.filter(tx => tx.debtorStore === filter || tx.creditorStore === filter);
  }, [transactions, filter]);

  const { totalHutang, totalTransaksi } = useMemo(() => {
    const { debts, payments } = filteredTransactions.reduce((acc, tx) => {
        const amount = Number(tx.amount);
        if (tx.type === 'bayar') acc.payments += amount;
        else acc.debts += amount;
        return acc;
    }, { debts: 0, payments: 0 });

    return { totalHutang: debts - payments, totalTransaksi: filteredTransactions.length };
  }, [filteredTransactions]);

  const sisaHutangDisplay = useMemo(() => {
    if (totalHutang > 0) return { text: `- ${formatCurrency(totalHutang)}`, color: 'text-red-400' };
    if (totalHutang < 0) return { text: formatCurrency(Math.abs(totalHutang)), color: 'text-green-400' };
    return { text: formatCurrency(0), color: 'text-gray-100' };
  }, [totalHutang]);

  const sendTelegramNotification = async (txData, type) => {
    const date = new Date().toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
    const message = `*${date}*\n${txData.debtorStore} ${type === 'hutang' ? 'Hutang' : 'Bayar'} ke ${txData.creditorStore} ${formatCurrency(txData.amount)}\n*Dibuat oleh: ${userData.email}*`;
    try {
      await fetch(`https://api.telegram.org/bot${TELEGRAM_API_TOKEN}/sendMessage`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: TELEGRAM_GROUP_ID, text: message, parse_mode: 'Markdown' }),
      });
    } catch (error) { console.error("Failed to send Telegram notification:", error); }
  };

  if (reportStore) return <StoreReport storeName={reportStore} allTransactions={transactions} onBack={() => setReportStore(null)} />;
  if (currentView === 'logs') return <ActivityLogPage onBack={() => setCurrentView('dashboard')} />;

  return (
    <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto space-y-8">
      <ConfirmationModal show={showDeleteConfirm} onClose={() => setShowDeleteConfirm(false)} onConfirm={handleDeleteConfirm} title="Konfirmasi Hapus" message="Apakah Anda yakin ingin menghapus data transaksi ini secara permanen?" />
      <PaymentModal show={showPaymentModal} onClose={() => setShowPaymentModal(false)} onSubmit={handlePaymentSubmit} transaction={payingTx} />
      <TransactionModal 
        show={showTransactionModal}
        onClose={() => { setShowTransactionModal(false); setEditingTx(null); }}
        onSubmit={handleTransactionSubmit}
        accessibleStores={accessibleStores}
        editingTx={editingTx}
      />
        
      {userData.role === 'superadmin' && (
          <SuperAdminSummary transactions={transactions} onViewReport={setReportStore} onViewLogs={() => setCurrentView('logs')} />
      )}

      <div className="bg-gray-800/50 p-4 sm:p-6 rounded-xl border border-gray-700 space-y-6">
          <div className="flex justify-end">
              <button
                  onClick={() => { setEditingTx(null); setShowTransactionModal(true); }}
                  className="w-full sm:w-auto py-3 px-6 bg-cyan-600 hover:bg-cyan-700 rounded-lg font-bold text-white transition-transform transform hover:scale-105 flex items-center justify-center gap-2"
              >
                  <i className="fa-solid fa-plus"></i>
                  <span>Hutang Baru</span>
              </button>
          </div>
          
          <TransactionFilter stores={accessibleStores} currentFilter={filter} onFilterChange={setFilter} onViewReport={setReportStore} />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              <div className="bg-gray-800 p-4 sm:p-6 rounded-xl flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-yellow-500/20 text-yellow-400 rounded-full flex items-center justify-center text-xl border-2 border-yellow-500 flex-shrink-0">
                      <i className="fa-solid fa-money-bill-wave"></i>
                  </div>
                  <div>
                      <p className="text-gray-400 text-sm">Sisa Hutang (Filter)</p>
                      <p className={`text-xl sm:text-2xl font-bold ${sisaHutangDisplay.color}`}>{sisaHutangDisplay.text}</p>
                  </div>
              </div>
               <div className="bg-gray-800 p-4 sm:p-6 rounded-xl flex items-center gap-4">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-cyan-500/20 text-cyan-400 rounded-full flex items-center justify-center text-xl border-2 border-cyan-500 flex-shrink-0">
                      <i className="fa-solid fa-arrow-right-arrow-left"></i>
                  </div>
                  <div>
                      <p className="text-gray-400 text-sm">Jumlah Transaksi (Filter)</p>
                      <p className="text-xl sm:text-2xl font-bold text-gray-100">{totalTransaksi}</p>
                  </div>
              </div>
          </div>
      </div>

      <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
        <h2 className="text-xl font-bold text-gray-100 p-4 sm:p-6 border-b border-gray-700 flex items-center gap-3">
          <i className="fa-solid fa-list-ul"></i>
          <span>Daftar Transaksi</span>
        </h2>
        {loading ? (
          <div className="p-12"><LoadingSpinner /></div>
        ) : (
          <TransactionList 
            transactions={filteredTransactions} 
            onEdit={handleEdit} 
            onDelete={handleDeleteRequest} 
            onPay={handlePaymentRequest} 
            accessibleStores={accessibleStores} 
          />
        )}
      </div>
    </main>
  );
}

// --- KOMPONEN RINGKASAN SUPERADMIN ---
function SuperAdminSummary({ transactions, onViewReport, onViewLogs }) {
    const summaryData = useMemo(() => {
        const storeStats = STORE_NAMES.reduce((acc, store) => {
            acc[store] = { totalPiutang: 0, totalHutang: 0 };
            return acc;
        }, {});

        transactions.forEach(tx => {
            const amount = Number(tx.amount);
            if (tx.type === 'hutang') {
                if (storeStats[tx.creditorStore]) storeStats[tx.creditorStore].totalPiutang += amount;
                if (storeStats[tx.debtorStore]) storeStats[tx.debtorStore].totalHutang += amount;
            } else if (tx.type === 'bayar') {
                if (storeStats[tx.creditorStore]) storeStats[tx.creditorStore].totalPiutang -= amount;
                if (storeStats[tx.debtorStore]) storeStats[tx.debtorStore].totalHutang -= amount;
            }
        });

        return STORE_NAMES.map(store => ({
            store,
            ...storeStats[store],
            posisiBersih: storeStats[store].totalPiutang - storeStats[store].totalHutang
        }));
    }, [transactions]);

    return (
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-700 flex justify-between items-center">
                <h2 className="text-xl font-bold text-gray-100 flex items-center gap-3">
                    <i className="fa-solid fa-shield-halved text-purple-400"></i>
                    <span>Ringkasan Global</span>
                </h2>
                <button onClick={onViewLogs} className="py-2 px-4 bg-yellow-600 hover:bg-yellow-700 rounded-lg font-semibold text-white text-sm flex items-center gap-2">
                    <i className="fa-solid fa-clipboard-list"></i>
                    <span>Log Aktivitas</span>
                </button>
            </div>
            <div className="overflow-x-auto">
                <table className="min-w-full">
                    <thead className="bg-gray-800/60">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase">Toko</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase">Piutang</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase">Hutang</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase">Bersih</th>
                            <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase">Aksi</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {summaryData.map(item => (
                            <tr key={item.store} className="hover:bg-gray-700/50">
                                <td className="px-6 py-4 text-sm font-medium">{item.store}</td>
                                <td className="px-6 py-4 text-sm text-green-400">{formatCurrency(item.totalPiutang)}</td>
                                <td className="px-6 py-4 text-sm text-red-400">{formatCurrency(item.totalHutang)}</td>
                                <td className={`px-6 py-4 text-sm font-bold ${item.posisiBersih >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                    {formatCurrency(item.posisiBersih)}
                                </td>
                                <td className="px-6 py-4">
                                    <button onClick={() => onViewReport(item.store)} className="py-1 px-3 bg-blue-600 rounded text-xs">Detail</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


// --- KOMPONEN FILTER ---
function TransactionFilter({ stores, currentFilter, onFilterChange, onViewReport }) {
    return (
      <div className="flex flex-col sm:flex-row gap-4 w-full">
        <div className="flex-grow">
            <InputWrapper icon="fa-filter" label="Filter Toko">
                 <select value={currentFilter} onChange={(e) => onFilterChange(e.target.value)} className="w-full bg-transparent">
                    <option className="bg-gray-800" value="all">Semua Toko</option>
                    {stores.map(store => <option className="bg-gray-800" key={store} value={store}>{store}</option>)}
                </select>
            </InputWrapper>
        </div>
        <button 
            onClick={() => currentFilter !== 'all' && onViewReport(currentFilter)} 
            disabled={currentFilter === 'all'}
            className="sm:mt-6 h-[52px] px-6 bg-purple-600 rounded-lg font-bold disabled:bg-gray-600"
        >
            Laporan
        </button>
      </div>
    );
}

// --- KOMPONEN MODAL TRANSAKSI ---
function TransactionModal({ show, onClose, onSubmit, accessibleStores, editingTx }) {
    if (!show) return null;
    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl max-w-2xl w-full border border-gray-700 relative p-6 sm:p-8">
                 <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-gray-300">
                    <i className="fa-solid fa-times text-2xl"></i>
                </button>
                <TransactionForm onSubmit={onSubmit} accessibleStores={accessibleStores} editingTx={editingTx} onCancelEdit={onClose} />
            </div>
        </div>
    );
}

// --- KOMPONEN FORM TRANSAKSI ---
function TransactionForm({ onSubmit, accessibleStores, editingTx, onCancelEdit }) {
  const initialFormState = { debtorStore: '', creditorStore: '', amount: '', description: '', date: new Date().toISOString().split('T')[0], debtorAccountSource: '', creditorAccountSource: '' };
  const [formState, setFormState] = useState(initialFormState);
  const [error, setError] = useState(null);

  const formatNumber = (value) => {
    if (!value) return '';
    const numeric = String(value).replace(/\./g, '');
    return isNaN(numeric) ? '' : new Intl.NumberFormat('id-ID').format(numeric);
  };

  useEffect(() => {
    if (editingTx) {
      setFormState({ ...editingTx, amount: formatNumber(editingTx.amount) });
    } else {
      setFormState(initialFormState);
    }
  }, [editingTx]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'amount') {
      setFormState(prev => ({ ...prev, amount: formatNumber(value.replace(/[^0-9]/g, '')) }));
    } else {
      setFormState(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (formState.debtorStore === formState.creditorStore) return setError("Toko peminjam & pemberi tidak boleh sama.");
    const numericAmount = parseInt(formState.amount.replace(/\./g, ''), 10);
    if (!numericAmount) return setError("Jumlah harus valid.");
    onSubmit({ ...formState, amount: numericAmount }, !!editingTx);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <AlertModal show={!!error} onClose={() => setError(null)} title="Validasi" message={error} />
      <h2 className="text-xl font-bold text-center mb-4">{editingTx ? 'Edit' : 'Tambah'} Hutang</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputWrapper icon="fa-store" label="Penerima Dana (Hutang)">
              <select name="debtorStore" value={formState.debtorStore} onChange={handleChange} required className="w-full bg-transparent">
                  <option className="bg-gray-800" value="">Pilih...</option>
                  {accessibleStores.map(s => <option className="bg-gray-800" key={s} value={s}>{s}</option>)}
              </select>
          </InputWrapper>
          <InputWrapper icon="fa-shop" label="Pemberi Dana">
              <select name="creditorStore" value={formState.creditorStore} onChange={handleChange} required className="w-full bg-transparent">
                  <option className="bg-gray-800" value="">Pilih...</option>
                  {STORE_NAMES.map(s => <option className="bg-gray-800" key={s} value={s}>{s}</option>)}
              </select>
          </InputWrapper>
          <InputWrapper icon="fa-wallet" label="Rek. Penerima">
              <select name="debtorAccountSource" value={formState.debtorAccountSource} onChange={handleChange} required className="w-full bg-transparent">
                  <option className="bg-gray-800" value="">Pilih...</option>
                  <option className="bg-gray-800" value="Kaki 3">Kaki 3</option>
                  <option className="bg-gray-800" value="Tampung">Tampung</option>
              </select>
          </InputWrapper>
          <InputWrapper icon="fa-wallet" label="Rek. Pemberi">
              <select name="creditorAccountSource" value={formState.creditorAccountSource} onChange={handleChange} required className="w-full bg-transparent">
                  <option className="bg-gray-800" value="">Pilih...</option>
                  <option className="bg-gray-800" value="Kaki 3">Kaki 3</option>
                  <option className="bg-gray-800" value="Tampung">Tampung</option>
              </select>
          </InputWrapper>
          <InputWrapper icon="fa-money-bill" label="Jumlah">
              <input type="text" name="amount" value={formState.amount} onChange={handleChange} required className="w-full bg-transparent" />
          </InputWrapper>
          <InputWrapper icon="fa-calendar" label="Tanggal">
              <input type="date" name="date" value={formState.date} onChange={handleChange} required className="w-full bg-transparent" />
          </InputWrapper>
      </div>
      <InputWrapper icon="fa-pencil" label="Keterangan">
          <textarea name="description" value={formState.description} onChange={handleChange} className="w-full bg-transparent"></textarea>
      </InputWrapper>
      <div className="flex justify-end gap-3 mt-4">
        <button type="button" onClick={onCancelEdit} className="px-4 py-2 bg-gray-600 rounded">Batal</button>
        <button type="submit" className="px-4 py-2 bg-cyan-600 rounded font-bold">Simpan</button>
      </div>
    </form>
  );
}

const InputWrapper = ({ icon, label, children }) => (
    <div className="w-full">
        <label className="text-xs text-gray-400 ml-1">{label}</label>
        <div className="flex items-center gap-2 bg-gray-700 border border-gray-600 rounded-lg p-3 focus-within:border-cyan-500">
            <i className={`fa-solid ${icon} text-gray-400 w-4`}></i>
            {children}
        </div>
    </div>
);

// --- KOMPONEN DAFTAR TRANSAKSI ---
function TransactionList({ transactions, onEdit, onDelete, onPay, accessibleStores }) {
  if (transactions.length === 0) return <div className="p-12 text-center text-gray-500">Kosong.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-800/60 uppercase text-xs">
          <tr><th className="px-6 py-3 text-left">Tipe</th><th className="px-6 py-3 text-left">Dari</th><th className="px-6 py-3 text-left">Ke</th><th className="px-6 py-3 text-left">Jumlah</th><th className="px-6 py-3 text-left">Waktu</th><th className="px-6 py-3 text-right">Aksi</th></tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {transactions.map(tx => {
            const isDebt = tx.type !== 'bayar';
            const canAction = accessibleStores.includes(tx.debtorStore);
            return (
              <tr key={tx.id} className="hover:bg-gray-800/40">
                <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded text-[10px] font-bold ${isDebt ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'}`}>{isDebt ? 'HUTANG' : 'BAYAR'}</span></td>
                <td className="px-6 py-4">{isDebt ? tx.creditorStore : tx.debtorStore}</td>
                <td className="px-6 py-4">{isDebt ? tx.debtorStore : tx.creditorStore}</td>
                <td className={`px-6 py-4 font-bold ${isDebt ? 'text-red-400' : 'text-green-400'}`}>{formatCurrency(tx.amount)}</td>
                <td className="px-6 py-4 text-xs text-gray-400">{formatDateTime(tx.createdAt)}</td>
                <td className="px-6 py-4 text-right space-x-2">
                  {canAction && (
                    <>
                      {isDebt && <button onClick={() => onPay(tx)} className="text-green-400 hover:underline">Bayar</button>}
                      <button onClick={() => onEdit(tx)} className="text-cyan-400 hover:underline">Edit</button>
                      <button onClick={() => onDelete(tx)} className="text-red-400 hover:underline">Hapus</button>
                    </>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- KOMPONEN BANTUAN ---
function LoadingSpinner() { return <div className="fixed inset-0 flex items-center justify-center bg-gray-900 z-50"><div className="animate-spin rounded-full h-12 w-12 border-t-2 border-cyan-500"></div></div>; }
function AlertModal({ show, onClose, title, message }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[100] p-4">
      <div className="bg-gray-800 rounded-xl p-6 max-w-sm w-full text-center border border-gray-700">
        <h2 className="font-bold mb-2">{title}</h2>
        <p className="text-gray-400 text-sm mb-4">{message}</p>
        <button onClick={onClose} className="bg-cyan-600 px-6 py-2 rounded font-bold w-full">OK</button>
      </div>
    </div>
  );
}

function ConfirmationModal({ show, onClose, onConfirm, title, message }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 text-center">
        <h2 className="font-bold mb-2">{title}</h2>
        <p className="text-sm text-gray-400 mb-6">{message}</p>
        <div className="flex gap-4">
          <button onClick={onClose} className="flex-1 px-4 py-2 bg-gray-700 rounded">Batal</button>
          <button onClick={onConfirm} className="flex-1 px-4 py-2 bg-red-600 rounded font-bold">Hapus</button>
        </div>
      </div>
    </div>
  );
}

function PaymentModal({ show, onClose, onSubmit, transaction }) {
  const [form, setForm] = useState({ amount: '', date: new Date().toISOString().split('T')[0], description: '' });
  if (!show || !transaction) return null;
  const handleSubmit = (e) => {
    e.preventDefault();
    const num = parseInt(form.amount.replace(/\./g, ''), 10);
    if (!num) return;
    onSubmit({ debtorStore: transaction.debtorStore, creditorStore: transaction.creditorStore, amount: num, date: form.date, description: form.description || "Pembayaran hutang", debtorAccountSource: transaction.debtorAccountSource, creditorAccountSource: transaction.creditorAccountSource });
  };
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 w-full max-w-md">
        <h2 className="font-bold mb-4">Bayar Hutang</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <InputWrapper icon="fa-money-bill" label="Jumlah">
            <input type="text" value={form.amount} onChange={e => setForm({...form, amount: new Intl.NumberFormat('id-ID').format(e.target.value.replace(/\D/g,''))})} required className="w-full bg-transparent" />
          </InputWrapper>
          <InputWrapper icon="fa-calendar" label="Tanggal">
            <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required className="w-full bg-transparent" />
          </InputWrapper>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 py-2 bg-gray-700 rounded">Batal</button>
            <button type="submit" className="flex-1 py-2 bg-green-600 rounded font-bold">Bayar</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- KOMPONEN LAPORAN TOKO (DENGAN FIX ERROR) ---
function StoreReport({ storeName, allTransactions, onBack }) {
    const reportData = useMemo(() => {
        // Objek dinamis agar tidak error jika ada nama toko yang typo di database
        const relations = {}; 

        allTransactions.forEach(tx => {
            const amount = Number(tx.amount) || 0;
            const isDebt = tx.type !== 'bayar';
            
            // Mencari pihak lawan
            let other;
            let role; // 'debtor' (kita berhutang) atau 'creditor' (kita memberi piutang)

            if (tx.creditorStore === storeName) {
                other = tx.debtorStore;
                role = 'creditor';
            } else if (tx.debtorStore === storeName) {
                other = tx.creditorStore;
                role = 'debtor';
            }

            if (other && other !== storeName) {
                if (!relations[other]) {
                    relations[other] = { totalDebtTo: 0, totalPaidTo: 0, totalDebtFrom: 0, totalPaidFrom: 0 };
                }

                if (role === 'creditor') {
                    if (isDebt) relations[other].totalDebtTo += amount;
                    else relations[other].totalPaidTo += amount;
                } else {
                    if (isDebt) relations[other].totalDebtFrom += amount;
                    else relations[other].totalPaidFrom += amount;
                }
            }
        });

        const debtsToStore = [], debtsFromStore = [];
        let totalReceivables = 0, totalPayables = 0;

        Object.keys(relations).forEach(otherStore => {
            const data = relations[otherStore];
            const remainingDebtTo = data.totalDebtTo - data.totalPaidTo;
            if (remainingDebtTo > 0) {
                debtsToStore.push({ store: otherStore, ...data, remaining: remainingDebtTo });
                totalReceivables += remainingDebtTo;
            }

            const remainingDebtFrom = data.totalDebtFrom - data.totalPaidFrom;
            if (remainingDebtFrom > 0) {
                debtsFromStore.push({ store: otherStore, ...data, remaining: remainingDebtFrom });
                totalPayables += remainingDebtFrom;
            }
        });
        
        return { debtsToStore, debtsFromStore, totalReceivables, totalPayables };
    }, [storeName, allTransactions]);

    const transactionHistory = useMemo(() => {
        return allTransactions
            .filter(tx => tx.debtorStore === storeName || tx.creditorStore === storeName)
            .sort((a, b) => {
                const timeA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : 0;
                const timeB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : 0;
                return timeB - timeA;
            });
    }, [storeName, allTransactions]);

    const handleExportExcel = () => {
        if (!window.XLSX) return alert("Pustaka Excel belum siap.");
        const wb = window.XLSX.utils.book_new();
        const wsData = transactionHistory.map(tx => ({
            'Waktu': formatDateTime(tx.createdAt),
            'Tipe': tx.type.toUpperCase(),
            'Peminjam': tx.debtorStore,
            'Pemberi': tx.creditorStore,
            'Jumlah': tx.amount,
            'Ket': tx.description || '-'
        }));
        const ws = window.XLSX.utils.json_to_sheet(wsData);
        window.XLSX.utils.book_append_sheet(wb, ws, "History");
        window.XLSX.writeFile(wb, `Report_${storeName}.xlsx`);
    };

    return (
        <main className="p-4 sm:p-8 max-w-7xl mx-auto space-y-8 animate-fade-in">
            <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
                <div>
                    <h1 className="text-2xl font-bold">Laporan: <span className="text-cyan-400">{storeName}</span></h1>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                    <button onClick={handleExportExcel} className="flex-1 sm:flex-none px-4 py-2 bg-green-600 rounded font-bold"><i className="fa-solid fa-file-excel mr-2"></i>Export</button>
                    <button onClick={onBack} className="flex-1 sm:flex-none px-4 py-2 bg-gray-600 rounded font-bold">Kembali</button>
                </div>
            </div>

             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
                    <p className="text-gray-400 text-xs mb-1">TOTAL PIUTANG (Uang di Toko Lain)</p>
                    <p className="text-2xl font-bold text-green-400">{formatCurrency(reportData.totalReceivables)}</p>
                </div>
                <div className="bg-gray-800 p-6 rounded-xl border border-gray-700">
                    <p className="text-gray-400 text-xs mb-1">TOTAL HUTANG (Uang Toko Lain di Sini)</p>
                    <p className="text-2xl font-bold text-red-400">{formatCurrency(reportData.totalPayables)}</p>
                </div>
            </div>

            <ReportTable title={`Pihak yang Berhutang ke ${storeName}`} data={reportData.debtsToStore} type="receivable" />
            <ReportTable title={`Daftar Hutang ${storeName} ke Pihak Lain`} data={reportData.debtsFromStore} type="payable" />
            <ReportTransactionHistory title="Riwayat Transaksi" transactions={transactionHistory} storeName={storeName} />
        </main>
    );
}

function ReportTable({ title, data, type }) {
    return (
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
             <h2 className="text-sm font-bold p-4 border-b border-gray-700 uppercase tracking-wider">{title}</h2>
             {data.length === 0 ? <p className="p-6 text-gray-500 text-sm italic">Tidak ada data.</p> : (
                <div className="overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-900/40">
                        <tr><th className="px-6 py-3 text-left">Nama Toko</th><th className="px-6 py-3 text-left">Total Pinjaman</th><th className="px-6 py-3 text-left">Sisa</th></tr>
                      </thead>
                      <tbody className="divide-y divide-gray-700">
                        {data.map((item) => (
                          <tr key={item.store} className="hover:bg-gray-700/30">
                            <td className="px-6 py-4 font-medium">{item.store}</td>
                            <td className="px-6 py-4">{formatCurrency(type === 'receivable' ? item.totalDebtTo : item.totalDebtFrom)}</td>
                            <td className="px-6 py-4 font-bold text-red-400">{formatCurrency(item.remaining)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                </div>
             )}
        </div>
    )
}

function ReportTransactionHistory({ title, transactions, storeName }) {
    return (
        <div className="bg-gray-800/50 rounded-xl border border-gray-700 overflow-hidden">
            <h2 className="text-sm font-bold p-4 border-b border-gray-700 uppercase tracking-wider">{title}</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full text-xs">
                    <thead className="bg-gray-900/40">
                        <tr><th className="px-6 py-3 text-left">Waktu</th><th className="px-6 py-3 text-left">Aksi</th><th className="px-6 py-3 text-left">Lawan</th><th className="px-6 py-3 text-left">Jumlah</th></tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700">
                        {transactions.map((tx) => {
                            const details = getTransactionDetails(tx, storeName);
                            return (
                                <tr key={tx.id} className="hover:bg-gray-700/30">
                                    <td className="px-6 py-4 text-gray-400">{formatDateTime(tx.createdAt)}</td>
                                    <td className="px-6 py-4 font-medium">{details.transactionType}</td>
                                    <td className="px-6 py-4">{details.otherParty}</td>
                                    <td className={`px-6 py-4 font-bold ${details.amountColor}`}>{formatCurrency(tx.amount)}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// --- LOG AKTIVITAS ---
function ActivityLogPage({ onBack }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    useEffect(() => {
        const unsubscribe = onSnapshot(query(collection(db, 'activityLogs'), orderBy('timestamp', 'desc')), (snapshot) => {
            setLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);
    return (
        <div className="p-4 sm:p-8 max-w-7xl mx-auto space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text-xl font-bold">Log Aktivitas</h1>
                <button onClick={onBack} className="px-4 py-2 bg-gray-600 rounded">Kembali</button>
            </div>
            {loading ? <LoadingSpinner /> : (
                <div className="bg-gray-800 border border-gray-700 rounded-xl overflow-x-auto">
                    <table className="min-w-full text-xs">
                        <thead><tr className="bg-gray-900/50"><th className="px-6 py-3 text-left">Waktu</th><th className="px-6 py-3 text-left">User</th><th className="px-6 py-3 text-left">Aksi</th><th className="px-6 py-3 text-left">Detail</th></tr></thead>
                        <tbody className="divide-y divide-gray-700">
                            {logs.map(log => (
                                <tr key={log.id} className="hover:bg-gray-700/30">
                                    <td className="px-6 py-4 text-gray-400 whitespace-nowrap">{formatDateTime(log.timestamp)}</td>
                                    <td className="px-6 py-4 text-cyan-400">{log.userEmail}</td>
                                    <td className="px-6 py-4 font-medium">{log.action}</td>
                                    <td className="px-6 py-4 text-gray-300">{log.details}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// --- FUNGSI BANTUAN ---
const getTransactionDetails = (tx, storeName) => {
    const isDebt = tx.type !== 'bayar';
    const isPemberi = tx.creditorStore === storeName;
    const otherParty = isPemberi ? tx.debtorStore : tx.creditorStore;
    let transactionType, amountColor;
    if (isDebt) {
        transactionType = isPemberi ? "MEMBERI HUTANG" : "MENERIMA HUTANG";
        amountColor = isPemberi ? "text-red-400" : "text-yellow-400";
    } else {
        transactionType = isPemberi ? "TERIMA BAYARAN" : "MEMBAYAR HUTANG";
        amountColor = "text-green-400";
    }
    return { transactionType, amountColor, otherParty };
};

const formatCurrency = (amount) => new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount || 0);
const formatDateTime = (timestamp) => {
  if (!timestamp || !timestamp.toDate) return '-';
  return new Intl.DateTimeFormat('id-ID', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(timestamp.toDate());
};